import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { api } from '../services/api';
import './ContractDetailPage.css';

const STATUS_LABEL = { Draft: 'Draft', Sent: 'Sent', Signed: 'Signed' };
const STATUS_CLASS = { Draft: 'badge-draft', Sent: 'badge-sent', Signed: 'badge-signed' };

const SIG_FONTS = { 'great-vibes': 'Great Vibes', allura: 'Allura', sacramento: 'Sacramento' };
const sigFamily = (k) => `'${SIG_FONTS[k] || 'Allura'}', cursive`;

const fmtMoney = (n) =>
  (n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) =>
  new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

export default function ContractDetailPage() {
  const { id }       = useParams();
  const navigate     = useNavigate();
  const [contract, setContract] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [sending, setSending]   = useState(false);
  const [error, setError]       = useState(null);
  const [copied, setCopied]     = useState(false);

  const load = () => api.get(`/contracts/${id}`).then(c => { setContract(c); setLoading(false); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  async function handleSend() {
    if (!window.confirm('Send this contract to the client for signing?')) return;
    setSending(true);
    setError(null);
    try {
      await api.post(`/contracts/${id}/send`);
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSending(false);
    }
  }

  function signingLink() {
    return `${window.location.origin}/sign/${contract.signingToken}`;
  }

  function copyLink() {
    const text = signingLink();
    if (navigator.clipboard && document.hasFocus()) {
      navigator.clipboard.writeText(text).then(() => flashCopied());
    } else {
      // Fallback for when document isn't focused or Clipboard API unavailable
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.cssText = 'position:fixed;opacity:0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      flashCopied();
    }
  }

  function flashCopied() {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  async function downloadPdf() {
    setError(null);
    try {
      const blob = await api.getBlob(`/contracts/${id}/pdf`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${contract.title}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message);
    }
  }

  if (loading) return <div className="contract-detail loading">Loading…</div>;

  const signed = contract.status === 'Signed';

  return (
    <div className="contract-detail">
      <button className="cd-back" onClick={() => navigate('/contracts')}>← Contracts</button>

      <header className="cd-header">
        <div className="cd-heading">
          <span className={`cd-badge ${STATUS_CLASS[contract.status]}`}>
            <span className="cd-dot" />{STATUS_LABEL[contract.status]}
          </span>
          <h2 className="cd-title">{contract.title}</h2>
          <p className="cd-parties">
            {contract.clientName}
            {contract.company && <span className="cd-party-sep"> · {contract.company}</span>}
          </p>
        </div>
        <div className="cd-actions">
          {contract.status === 'Draft' && (
            <button className="cd-send" onClick={handleSend} disabled={sending}>
              {sending ? 'Sending…' : 'Send for Signature'}
            </button>
          )}
          {signed && (
            <span className="cd-signed-stamp">
              <span className="cd-check">✓</span> Signed {fmtDate(contract.signedAt)}
            </span>
          )}
        </div>
      </header>

      {error && <div className="cd-error">{error}</div>}

      <section className="cd-summary">
        <div className="cd-figure">
          <span className="cd-fig-label">Setup Fee</span>
          <span className="cd-fig-value">${fmtMoney(contract.setupFee)}</span>
        </div>
        <div className="cd-figure">
          <span className="cd-fig-label">Monthly Fee</span>
          <span className="cd-fig-value">${fmtMoney(contract.monthlyFee)}<span className="cd-fig-unit">/mo</span></span>
        </div>
        <div className="cd-figure">
          <span className="cd-fig-label">Created</span>
          <span className="cd-fig-value cd-fig-date">{fmtDate(contract.createdAt)}</span>
        </div>
        {signed && (
          <div className="cd-figure cd-figure-signed">
            <span className="cd-fig-label">Signed</span>
            <span className="cd-fig-value cd-fig-date">{fmtDate(contract.signedAt)}</span>
          </div>
        )}
      </section>

      {contract.status !== 'Draft' && (
        <section className="cd-link">
          <div className="cd-link-label">Signing Link</div>
          <div className="cd-link-bar">
            <span className="cd-link-url" title={signingLink()}>{signingLink()}</span>
            <button className="cd-copy" onClick={copyLink}>
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
          {signed && (
            <div className="cd-audit">
              Signed {new Date(contract.signedAt).toLocaleString()} · IP {contract.signedByIp}
            </div>
          )}
        </section>
      )}

      {signed && (
        <section className="cd-exec">
          <div className="cd-exec-head">
            <span className="cd-exec-label">Executed</span>
            {contract.pdfUrl && (
              <button className="cd-pdf-btn" onClick={downloadPdf}>↓ Download signed PDF</button>
            )}
          </div>
          <div className="cd-exec-grid">
            <div className="cd-sig">
              <div className="cd-sig-mark" style={{ fontFamily: sigFamily(contract.consultantSignatureFont) }}>
                {contract.consultantSignedName || 'Semper Gratus Inc.'}
              </div>
              <div className="cd-sig-rule" />
              <div className="cd-sig-party">Semper Gratus Inc.</div>
              <div className="cd-sig-meta">Phillip Burt, Founder</div>
            </div>
            <div className="cd-sig">
              {contract.signatureType === 'drawn' && contract.signatureData ? (
                <img className="cd-sig-img" src={contract.signatureData} alt="Client signature" />
              ) : (
                <div className="cd-sig-mark" style={{ fontFamily: sigFamily(contract.signatureFont) }}>
                  {contract.signatureName || contract.clientName}
                </div>
              )}
              <div className="cd-sig-rule" />
              <div className="cd-sig-party">{contract.company || contract.clientName}</div>
              <div className="cd-sig-meta">{contract.signatureName || contract.clientName}</div>
            </div>
          </div>
        </section>
      )}

      <section className="cd-body-section">
        <div className="cd-body-label">Agreement</div>
        <article className="cd-body" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(contract.body) }} />
      </section>
    </div>
  );
}
